import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';
import type { CmsClubFileNode, CmsFileBase } from 'src/helpers/cms-file-helper';
import { parseRelativeDirectory, findCmsFileNodeForClub } from 'src/helpers/cms-file-helper';
import { SiteContext } from 'src/context/site-context';
import type { LanguageIsoCode } from 'src/enums/language';
import type { ClubLocation, SharedContent } from 'src/models/cms/shared-content';
import type { ClubPageFeatures, NewClubPageFeatures } from 'src/models/cms/club-page-features';
import type { EnabledPages } from 'src/content/enabled-pages-helper';
import type { NavigationMenuContent } from 'src/models/cms/navigation-menu-content';
import type { BlazeStudioLandingContent } from 'src/models/cms/blaze-studio-landing-content';
import type { BlazeStudioFaqsPageContent } from 'src/models/cms/blaze-studio-faqs-content';
import type { PromotionContent } from 'src/models/cms/promotions-content';
import type { FooterContent } from 'src/models/cms/footer-content';

const cmsContentQuery = graphql`
  fragment _ClubDetails on ClubJson {
    clubId
    clubName
    hyphenatedClubName
  }

  fragment _Features on ClubJson {
    aDayInLife
    clubPageLiveChat
    clubOpeningHours
    clubOpeningHours
    contactUsPhone
    facebookFeed
    fullBookATour
    instagramFeed
    timetable
    poolOpeningHours
    landingClubDetails
  }

  fragment _NewFeatures on ClubJson {
    fullBookATour
    clubPageLiveChat
    poolOpeningHours
    clubOpeningHours
    timetable
  }

  fragment _MultiImageBlocks on ClubJson {
    multiImageBlocks {
      title
      description
      bottomDescription
      images {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      showVideo
      videoId
      button {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
    }
  }

  fragment _AtClub on ClubJson {
    atClub {
      showBlock
      title
      description
      imagesWithLink {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
        link
        title
        description
      }
    }
  }

  fragment _CarouselWithButton on ClubJson {
    carouselWithButton {
      showBlock
      footerButton {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
      title
      description
      carouselImages {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
        text
        subtext
        button {
          showButton
          buttonText
          buttonLink
          goToGlobal
          buttonStyle
        }
      }
    }
  }

  fragment _MultiImageLinkBlockContent on ClubJson {
    multiImageLinkBlock {
      showBlock
      title
      description
      imagesWithLink {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
        link
        title
        description
      }
      button {
        showButton
        buttonText
        buttonStyle
      }
    }
  }

  fragment _SingleImageBlocks on ClubJson {
    singleImageBlocks {
      ..._SingleImageBlockDetails
    }
  }

  fragment _SharedContent on ClubJson {
    comingSoonClubFinder
    comingSoonHeroStyling
    clubEmail
    additionalClubEmailAddresses {
      emailAddress
      emailLabel
    }
    clubLocation {
      lat
      lng
      text1
      text2
      url
    }
    clubFacilities {
      facility
      videoId
    }
    clubVideos {
      mediaType
      text
      reference
    }
    menuLink
    enquireNowButton {
      enquireNowButtonText
      enquireNowButtonLink
      goToGlobal
    }
    linkDropdownCta {
      enabled
      text
      dropdownOptions {
        displayText
        link
      }
    }
    enquiryEmailAddresses
    events {
      name
      time
      description
      location
      action
      showUntil
    }
    landingVideo {
      videoId
      videoPlaceholder {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    tourEmailAddresses
    defaultPromoPopup {
      disabled
      title
      description
      button {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
      secondaryButton {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
      useUserInactivityTrigger
      userInactivityParameters {
        ..._UserInactivityParameters
      }
    }
    alsoAt {
      showBlock
      title
      description
      theClub {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      familyAndKids {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      clubFacilities {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      relaxAndSocialise {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    clubBanner {
      showBanner
      text
      button {
        showButton
        buttonText
        buttonLink
        goToGlobal
        buttonStyle
      }
      bannerBackgroundColor
      bannerTextColor
    }
    ..._AtClub
    sharedMobileApp {
      showBlock
      mobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
    }
  }

  fragment _NewSharedContent on ClubJson {
    clubEmail
    comingSoonClubFinder
    clubLocation {
      lat
      lng
      text1
      text2
      url
    }
    phoneNumbers {
      membersPhoneNumber
      nonMembersPhoneNumber
      clubPhoneNumbers {
        description
        number
      }
    }
    enquiryEmailAddresses
    tourEmailAddresses
    nonMembersEmail
    clubThumbnailImage
    comingSoonClubFinder
    clubDefaultPopup {
      ..._PromoBlockWithImageClubDefaultPagePopup
    }
  }

  fragment _NavigationMenu on ClubJson {
    fitnessMenu {
      displayText
      link
      image {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    treatmentsMenu {
      displayText
      link
      image {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    sportsMenu {
      displayText
      link
      image {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    childrenMenu {
      displayText
      link
      image {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    navigationPagesToDisplay {
      mainPages {
        displayText
        link
      }
      secondaryPages {
        displayText
        link
      }
      clubFacilitiesPages {
        mobileNavIcon
        displayText
        link
        facility
      }
    }
    navigation {
      spa {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      pools {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      tennis {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      padel {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      gym {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      exerciseClasses {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      creche {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      kidsClasses {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
  }

  fragment _DeepLinkPages on ClubJson {
    ptDeepLinks {
      title
      description
      hero {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      davidLloydMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      harbourClubMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
    }
    onDemandDeepLink {
      title
      description
      hero {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      davidLloydMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      harbourClubMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
    }
    liveStreamDeepLink {
      title
      description
      hero {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      davidLloydMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      harbourClubMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
    }
    extrasDeepLink {
      title
      description
      hero {
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      davidLloydMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      harbourClubMobileApp {
        title
        description
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
    }
  }

  fragment _BlazeStudioLandingContent on ClubJson {
    hero {
      title
      description
      landingVideo {
        videoId
        videoPlaceholder {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
    }
    introduction {
      description
    }
    vimeoIframe {
      title
      vimeoId
    }
    clubSummaryList {
      bullets
    }
    blazeTrainers {
      description
      images {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
    }
    blazePackages {
      description
      packageCards {
        numberOfSessions
        priceOfPackage
      }
    }
    blazeTrackYourProgress {
      backgroundImage {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      overlayImage {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      title
      description
    }
  }

  fragment _BlazeStudioFaqsContent on ClubJson {
    title
    faqsContent {
      title
      questions {
        question
        answer
      }
    }
  }

  fragment _SharedLandingContent on ClubJson {
    clubPhoneNumbers {
      description
      number
    }
    defaultLandingPromotion {
      inlinePromo {
        description
        subtext
        contentPosition
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
        button {
          showButton
          buttonText
          buttonLink
          goToGlobal
          buttonStyle
        }
        landingFirstContentBlockTextColor
        landingFirstContentBlockTextSize
      }
      banner {
        button {
          showButton
          buttonText
          buttonLink
          goToGlobal
          buttonStyle
        }
        secondaryButton {
          showButton
          buttonText
          buttonLink
          goToGlobal
          buttonStyle
        }
        comingSoonText
      }
    }
  }

  fragment _MemberOffersContent on ClubJson {
    bannerImage {
      imagePath
      alternateText
      horizontalFocus
      leftHighlight
    }
    bannerTitle
    bannerDescription
    memberOffers {
      backgroundImage {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      logo {
        imagePath
        alternateText
        horizontalFocus
        leftHighlight
      }
      brandName
      offerName
      title
      description
      offerPageTitle
      detailedDescription
      url
      validUntil
    }
  }

  fragment _QuadrantTextBlocks on ClubJson {
    quadrantTextBlocks {
      title
      description
    }
  }

  fragment _Pages on ClubJson {
    yogaPage {
      enabled
      clubSection
    }
    golfPage {
      enabled
      clubSection
    }
    familyAndKidsPage {
      enabled
      clubSection
    }
    relaxAndSocialisePage {
      enabled
      clubSection
    }
    timetablePage {
      enabled
      clubSection
    }
    clubFacilitiesPage {
      enabled
      clubSection
    }
    clubFacilities_spaPage {
      enabled
      clubSection
    }
    clubFacilities_poolsPage {
      enabled
      clubSection
    }
    clubFacilities_tennisPage {
      enabled
      clubSection
    }
    clubFacilities_padelPage {
      enabled
      clubSection
    }
    clubFacilities_gymPage {
      enabled
      clubSection
    }
    clubFacilities_exerciseClassesPage {
      enabled
      clubSection
    }
    clubFacilities_crechePage {
      enabled
      clubSection
    }
    clubFacilities_kidsClassesPage {
      enabled
      clubSection
    }
  }

  fragment _Placeholder on ClubJson {
    clubId
  }

  fragment _ServiceTypesBlock on ClubJson {
    serviceTypes {
      showBlock
      title
      serviceTypesList {
        title
        description
        characteristics
        image {
          imagePath
          alternateText
          horizontalFocus
          leftHighlight
        }
      }
      toggleButton {
        showButton
        buttonText
        buttonStyle
      }
    }
  }

  query CmsContentQuery {
    clubDetails: allFile(filter: { base: { eq: "details.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._ClubDetails
        }
      }
    }
    features: allFile(filter: { base: { eq: "features.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._Features
        }
      }
    }
    newFeatures: allFile(filter: { base: { eq: "new-features.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._NewFeatures
        }
      }
    }
    sharedContent: allFile(filter: { base: { eq: "shared.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._SharedContent
        }
      }
    }
    newSharedContent: allFile(filter: { base: { eq: "new-shared.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._NewSharedContent
        }
      }
    }
    navigationMenuContent: allFile(filter: { base: { eq: "navigation-menu.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._NavigationMenu
        }
      }
    }
    promotionsContent: allFile(filter: { relativeDirectory: { eq: "cross-club/promotions" } }) {
      nodes {
        relativeDirectory
        childPromotionsJson {
          ... on PromotionsJson {
            en {
              ..._MainTimedPromotionContent
            }
            ca {
              ..._AlternateTimedPromotionContent
            }
            de {
              ..._AlternateTimedPromotionContent
            }
            es {
              ..._AlternateTimedPromotionContent
            }
            fr {
              ..._AlternateTimedPromotionContent
            }
            it {
              ..._AlternateTimedPromotionContent
            }
            nl {
              ..._AlternateTimedPromotionContent
            }
          }
        }
      }
    }
    newPromotionsContent: allFile(
      filter: { relativeDirectory: { eq: "cross-club/new-promotions" } }
    ) {
      nodes {
        relativeDirectory
        childNewPromotionsJson {
          ... on NewPromotionsJson {
            en {
              ..._MainNewClubPromotionContent
            }
            ca {
              ..._AlternateNewClubPromotionContent
            }
            de {
              ..._AlternateNewClubPromotionContent
            }
            es {
              ..._AlternateNewClubPromotionContent
            }
            fr {
              ..._AlternateNewClubPromotionContent
            }
            it {
              ..._AlternateNewClubPromotionContent
            }
            nl {
              ..._AlternateNewClubPromotionContent
            }
          }
        }
      }
    }
    deepLinkPages: allFile(filter: { base: { eq: "deep-links.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._DeepLinkPages
        }
      }
    }
    blazeStudioLandingContent: allFile(filter: { base: { eq: "blaze-landing.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._BlazeStudioLandingContent
        }
      }
    }
    blazeStudioFaqsContent: allFile(filter: { base: { eq: "blaze-faqs.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._BlazeStudioFaqsContent
        }
      }
    }
    sharedLandingContent: allFile(filter: { base: { eq: "landing.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._SharedLandingContent
        }
      }
    }
    memberOffersContent: allFile(filter: { base: { eq: "member-offers.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._MemberOffersContent
        }
      }
    }
    pages: allFile(filter: { base: { eq: "pages.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._Pages
        }
      }
    }
    headerContent: allFile(filter: { base: { eq: "header.json" } }) {
      nodes {
        relativeDirectory
        childClubJson {
          ..._ClubHeaderContent
        }
      }
    }
  }
`;

interface WrappedCmsRecord<T> {
  clubDetails: T;
  features: T;
  newFeatures: T;
  sharedContent: T;
  newSharedContent: T;
  navigationMenuContent: T;
  promotionsContent: T;
  newPromotionsContent: T;
  deepLinkPages: T;
  blazeStudioLandingContent: T;
  blazeStudioFaqsContent: T;
  sharedLandingContent: T;
  pages: T;
  memberOffersContent: T;
  footerContent: T;
  headerContent: T;
}

export type ClubHeaderContent = {
  headerMenuItems?: Queries.HeaderLevelOneMenuItem[];
  headerSecondaryUpperMenu?: Queries.HeaderSecondaryUpperMenuItem[];
  headerButtonPrimary?: Queries.RedesignButton;
  headerButtonSecondary?: Queries.RedesignButton;
  headerOtherText?: Queries.HeaderOtherText;
  headerPromoRibbon?: Queries.HeaderPromoRibbon;
};

export type NewSharedData = {
  clubEmail: string;
  clubLocation: ClubLocation;
  phoneNumbers: {
    membersPhoneNumber: string;
    nonMembersPhoneNumber: string;
    clubPhoneNumbers: Array<{ description?: string; number?: string }>;
  };
  tourEmailAddresses?: string[];
  enquiryEmailAddresses: string[];
  nonMembersEmail: string;
  clubThumbnailImage: string;
  comingSoonClubFinder: boolean;
  clubDefaultPopup?: Queries.PromoBlockWithImageClubDefaultPagePopup;
};

export interface CmsRecord {
  clubDetails: any;
  features: ClubPageFeatures;
  newFeatures: NewClubPageFeatures;
  sharedContent: SharedContent;
  newSharedContent: NewSharedData;
  navigationMenuContent: NavigationMenuContent;
  promotionsContent: Record<LanguageIsoCode, PromotionContent>[];
  newPromotionsContent: Record<LanguageIsoCode, Queries.NewClubPromotion>[];
  deepLinkPages: any;
  blazeStudioLandingContent: BlazeStudioLandingContent;
  blazeStudioFaqsContent: BlazeStudioFaqsPageContent;
  sharedLandingContent: Queries._SharedLandingContentFragment;
  pages: EnabledPages;
  memberOffersContent: any;
  footerContent: FooterContent;
  headerContent: ClubHeaderContent;
}

const keysWithMultipleNodes: (keyof CmsRecord)[] = ['promotionsContent', 'newPromotionsContent'];

type CmsPromotionFileNode<T> = CmsFileBase & {
  childPromotionsJson: T;
};

type CmsNewPromotionFileNode<T> = CmsFileBase & {
  childNewPromotionsJson: T;
};

type CmsRecordNode<T> = CmsClubFileNode<T> | CmsPromotionFileNode<T> | CmsNewPromotionFileNode<T>;

const extractChildFromNode = <T,>(node: CmsRecordNode<T>) =>
  'childClubJson' in node
    ? node.childClubJson
    : 'childPromotionsJson' in node
      ? node.childPromotionsJson
      : node.childNewPromotionsJson;

export const CmsProvider = ({
  clubId,
  children,
}: {
  clubId: string;
  children: (cmsContent: CmsRecord) => React.ReactNode;
}) => {
  const { language } = React.useContext(SiteContext);

  const cms = useStaticQuery<WrappedCmsRecord<{ nodes: CmsRecordNode<any>[] }>>(cmsContentQuery);

  const cmsRecord = React.useMemo(() => {
    // Replace backslashes in paths with forwards slashes for cross-platform compat
    (Object.keys(cms) as (keyof typeof cms)[]).forEach((key) => {
      cms[key].nodes.forEach(parseRelativeDirectory);
    });

    return (Object.keys(cms) as (keyof typeof cms)[]).reduce((record, key) => {
      if (keysWithMultipleNodes.includes(key)) {
        record[key] = cms[key].nodes.map(extractChildFromNode);
        return record;
      }

      const cmsNode = findCmsFileNodeForClub(cms[key].nodes, clubId, language);

      if (cmsNode) {
        record[key] = extractChildFromNode(cmsNode);
      } else {
        record[key] = null;
      }

      return record;
    }, {} as CmsRecord);
  }, [clubId, cms, language]);

  return children(cmsRecord);
};
